"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useVideoJSPlayer = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _slicedToArray2 = _interopRequireDefault(require("@babel/runtime/helpers/slicedToArray"));
var _react = require("react");
var _video = _interopRequireDefault(require("video.js"));
require("video.js/dist/video-js.css");
var _VideoPlayerHelper = require("../../helpers/VideoPlayerHelper");
var _defaultVolume = require("../../constants/defaultVolume");
var _utils = require("../../utils");
var _VideoPlayerContext = require("../../contexts/VideoPlayerContext");
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2["default"])(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var INITIAL_BANDWIDTH_IN_BPS = 3000000;
var normalizeDuration = function normalizeDuration(durationSeconds) {
  return Number.isFinite(durationSeconds) ? Math.trunc(durationSeconds) : 0;
};
var loggerName = 'VideoJSPlayer';
/**
 * Custom hook for integrating Video.js player with support for HLS streams,
 * quality filtering, and tracking video playback metrics such as seeking times.
 *
 * @param {string} sourceId - The sourceId of the media source.
 * @param {string} videoUrl - The URL of the video (could be an HLS `.m3u8` stream or a standard video file).
 * @param {Object} options - Options for the video player.
 * @param {boolean} options.isPlaying - Indicates whether the media is currently playing.
 * @param {boolean} options.loop - Indicates whether the media should loop.
 * @param {number} options.volume - The volume level of the media.
 * @param {boolean} options.muted - Indicates whether the media is muted.
 * @param {number} options.seek - The current seek position in the media.
 * @param {number} options.startedOn - The Unix timestamp when the media started playing.
 * @param {number} serverTime - The current server time as a Unix timestamp.
 * @param {number} serverTimeDelta - The difference between the client and server times in seconds.
 * @returns {Object} playerRef - A reference to the Video.js player instance.
 */
var useVideoJSPlayer = exports.useVideoJSPlayer = function useVideoJSPlayer(sourceId, videoUrl, _ref, serverTime, serverTimeDelta) {
  var _ref$isPlaying = _ref.isPlaying,
    isPlaying = _ref$isPlaying === void 0 ? false : _ref$isPlaying,
    _ref$loop = _ref.loop,
    loop = _ref$loop === void 0 ? false : _ref$loop,
    _ref$volume = _ref.volume,
    volume = _ref$volume === void 0 ? _defaultVolume.DEFAULT_VOLUME : _ref$volume,
    _ref$muted = _ref.muted,
    muted = _ref$muted === void 0 ? false : _ref$muted,
    _ref$seek = _ref.seek,
    seek = _ref$seek === void 0 ? 0 : _ref$seek,
    startedOn = _ref.startedOn;
  var playerRef = (0, _react.useRef)();
  var finishedRef = (0, _react.useRef)(false);
  var _useState = (0, _react.useState)(0),
    _useState2 = (0, _slicedToArray2["default"])(_useState, 2),
    duration = _useState2[0],
    setDuration = _useState2[1];
  var _useState3 = (0, _react.useState)(false),
    _useState4 = (0, _slicedToArray2["default"])(_useState3, 2),
    canPlay = _useState4[0],
    setCanPlay = _useState4[1];
  var videoPlayerContext = (0, _react.useContext)(_VideoPlayerContext.VideoPlayerContext);
  var videoSourceType = videoUrl !== null && videoUrl !== void 0 && videoUrl.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4';
  var videoJsOptions = (0, _react.useMemo)(function () {
    return {
      controls: false,
      responsive: true,
      fill: true,
      loop: loop,
      muted: muted,
      disablePictureInPicture: true,
      loadingSpinner: false,
      techOrder: ['html5'],
      html5: {
        vhs: {
          overrideNative: true,
          useDevicePixelRatio: true,
          // Favor sharper video on high-DPI
          enableLowInitialPlaylist: false,
          // Start with higher bitrate
          limitRenditionByPlayerDimensions: false,
          // allow the highest resolution
          initialBandwidth: INITIAL_BANDWIDTH_IN_BPS
        }
      },
      sources: [{
        src: videoUrl,
        type: videoSourceType
      }]
    };
  }, [videoUrl, loop, muted, videoSourceType]);
  (0, _react.useEffect)(function () {
    if (!videoUrl) return;
    var videoElement = document.getElementById("media_player_".concat(sourceId));
    if (videoElement && !playerRef.current) {
      var player = playerRef.current = (0, _video["default"])(videoElement, videoJsOptions);
      (0, _VideoPlayerHelper.setMediaSessionActions)(function () {});
      var handleLoadedMetadata = function handleLoadedMetadata() {
        var playerDuration = normalizeDuration(player.duration());
        setDuration(playerDuration);
        if (isPlaying) {
          // Video is still in progress or looping, start playback
          // https://videojs.org/blog/autoplay-best-practices-with-video-js/
          var playPromise = player.play();
          if (playPromise === undefined) {
            return;
          }
          playPromise["catch"](function (error) {
            console.log("[VideoJs] Autoplay failed for ID: ".concat(sourceId), error);
          });
        }
      };
      player.ready(function () {
        console.log("[VideoJs] Player ready with ID: ".concat(sourceId));
        videoPlayerContext.setPlayersInitialized(function (prevState) {
          return _objectSpread(_objectSpread({}, prevState), {}, (0, _defineProperty2["default"])({}, sourceId, true));
        });
        player.on('loadedmetadata', handleLoadedMetadata);
        player.on('durationchange', function () {
          setDuration(normalizeDuration(player.duration()));
        });
        player.on('timeupdate', function () {
          setDuration(normalizeDuration(player.duration()));
        });
        player.on('ended', function () {
          if (!loop) {
            setDuration(normalizeDuration(player.duration()));
            player.pause();
          }
        });
        player.on('error', function () {
          var error = player.error();
          (0, _utils.sendToDatadog)(loggerName, {
            error: error,
            sourceId: sourceId
          }, "VideoJS - An error occurred during video playback.", true);
        });
        player.volume(volume);
      });
      videoElement.player = player;
      return function () {
        player.off('loadedmetadata');
        player.off('durationchange');
        player.off('error');
        player.off('ended');
      };
    }
  }, [sourceId, videoJsOptions]);
  (0, _react.useEffect)(function () {
    if (!playerRef.current) return;
    var player = playerRef.current;
    var desiredCurrentTime = (0, _VideoPlayerHelper.getCurrentPlaybackPosition)(isPlaying, startedOn, seek, duration, serverTime, serverTimeDelta, loop);
    var isPlaybackFinished = desiredCurrentTime !== undefined && duration > 0 && desiredCurrentTime >= duration && !loop;
    if (isPlaybackFinished) {
      if (!finishedRef.current) {
        finishedRef.current = true;
        var endPosition = Math.max(0, player.duration() - 0.1);
        player.currentTime(endPosition);
        player.pause();
      }
    } else {
      finishedRef.current = false;
      (0, _VideoPlayerHelper.syncPlayerToDesiredTime)(player, {
        desiredCurrentTime: desiredCurrentTime,
        duration: duration,
        loop: loop,
        canPlay: canPlay,
        isPlaying: isPlaying,
        startedOn: startedOn
      });
    }
    var handleCanPlay = function handleCanPlay() {
      var _playerRef$current;
      setCanPlay(true);
      // When a muted HLS video re-buffers after a tab switch, video.js's
      // resumes playback even if the video is paused by the host.
      // Enforce the current isPlaying state to prevent unwanted auto-resume.
      if (!isPlaying && !((_playerRef$current = playerRef.current) !== null && _playerRef$current !== void 0 && _playerRef$current.paused())) {
        var _playerRef$current2;
        (_playerRef$current2 = playerRef.current) === null || _playerRef$current2 === void 0 || _playerRef$current2.pause();
      }
    };
    var visibilityChangeHandler = function visibilityChangeHandler() {
      (0, _VideoPlayerHelper.handleVisibilityRestore)(player, {
        isPlaying: isPlaying,
        startedOn: startedOn,
        seek: seek,
        duration: duration,
        serverTime: serverTime,
        serverTimeDelta: serverTimeDelta,
        loop: loop,
        canPlay: canPlay
      });
    };
    document.addEventListener('visibilitychange', visibilityChangeHandler);
    player.on('canplay', handleCanPlay);
    return function () {
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
      player.off('canplay', handleCanPlay);
    };
  }, [canPlay, isPlaying, seek, startedOn, serverTime, serverTimeDelta, duration]);
  (0, _react.useEffect)(function () {
    var player = playerRef.current;
    if (player) {
      player.loop(loop);
      player.volume(volume);
      player.muted(muted);
    }
  }, [loop, volume, muted]);
  (0, _react.useEffect)(function () {
    return function () {
      if (playerRef !== null && playerRef !== void 0 && playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
        console.log("[VideoJs] Player disposed with ID: ".concat(sourceId));
        (0, _VideoPlayerHelper.setMediaSessionActions)(null);
      }
    };
  }, []);
  return playerRef;
};